import { create } from 'zustand';

import { DELETE_LABEL_MODAL_LABELS } from 'src/apps/dashboard/pages/label-management/components/delete-label-confirm-modal/DeleteLabelConfirmModal.labels';
import { Label, LabelType, PanelMode } from 'src/apps/dashboard/pages/label-management/types';
import {
	deleteLabel as deleteLabelApi,
	fetchLabels as fetchLabelsApi,
	saveLabel as saveLabelApi
} from 'src/apps/dashboard/services/label-management';
import { Logger } from 'src/common/services/Logger';

import { LABEL_MANAGEMENT_STORE_LABELS } from './labelManagementStore.labels';

interface LabelManagementState {
	labels: Label[];
	isLoading: boolean;
	error: string | null;
	isSaving: boolean;
	isDeleting: boolean;
	isPanelOpen: boolean;
	panelMode: PanelMode;
	activeLabel: Label | null;
	labelToDelete: Label | null;
	deleteError: string | null;

	fetchLabels: (productId: string) => Promise<void>;
	saveLabel: (productId: string, tenantId: string, label: Label) => Promise<void>;
	deleteLabel: (productId: string, tenantId: string) => Promise<boolean>;
	openAddPanel: () => void;
	openEditPanel: (label: Label) => void;
	closePanel: () => void;
	requestDelete: (label: Label) => void;
	cancelDelete: () => void;
	reset: () => void;
}

const initialState = {
	labels: [] as Label[],
	isLoading: true,
	error: null as string | null,
	isSaving: false,
	isDeleting: false,
	isPanelOpen: false,
	panelMode: PanelMode.ADD,
	activeLabel: null,
	labelToDelete: null,
	deleteError: null
};

export const useLabelManagementStore = create<LabelManagementState>((set, get) => ({
	...initialState,

	fetchLabels: async (productId: string) => {
		if (!productId) {
			set({ isLoading: false, labels: [], error: null });
			return;
		}

		set({ isLoading: true, error: null });

		try {
			const labels = await fetchLabelsApi(productId);
			set({ labels, isLoading: false });
		} catch (error) {
			Logger.error('LabelManagementStore -> fetchLabels failed:', error);
			set({
				isLoading: false,
				error: LABEL_MANAGEMENT_STORE_LABELS.FETCH_LABELS_FAILED
			});
		}
	},

	saveLabel: async (productId: string, tenantId: string, label: Label) => {
		const { labels } = get();
		const currentCoreLabels = labels.filter(
			(existingLabel) => existingLabel.type === LabelType.CORE
		);
		const currentCustomLabels = labels.filter(
			(existingLabel) => existingLabel.type === LabelType.CUSTOM
		);
		set({ isSaving: true });

		try {
			await saveLabelApi(productId, tenantId, label, currentCoreLabels, currentCustomLabels);

			const latestLabels = get().labels;
			const existingIndex = latestLabels.findIndex(
				(existingLabel) => existingLabel.key === label.key
			);
			const updatedLabels =
				existingIndex >= 0
					? latestLabels.map((existingLabel) =>
							existingLabel.key === label.key ? label : existingLabel
						)
					: [...latestLabels, label];

			set({ labels: updatedLabels, isPanelOpen: false, activeLabel: null, isSaving: false });
		} catch (error) {
			Logger.error('LabelManagementStore -> saveLabel failed:', error);
			set({ isSaving: false });
			throw error;
		}
	},

	deleteLabel: async (productId: string, tenantId: string) => {
		const { labelToDelete, labels } = get();
		if (!labelToDelete) {
			return false;
		}

		const currentCoreLabels = labels.filter(
			(existingLabel) => existingLabel.type === LabelType.CORE
		);
		const currentCustomLabels = labels.filter(
			(existingLabel) => existingLabel.type === LabelType.CUSTOM
		);
		set({ isDeleting: true, deleteError: null });

		try {
			await deleteLabelApi(
				productId,
				tenantId,
				labelToDelete.key,
				labelToDelete.type,
				currentCoreLabels,
				currentCustomLabels
			);

			// Apply the deletion to local state after the API confirmed success:
			// core labels keep the entry but with values cleared; custom labels are removed entirely
			const latestLabels = get().labels;
			const updatedLabels =
				labelToDelete.type === LabelType.CORE
					? latestLabels.map((existingLabel) =>
							existingLabel.key === labelToDelete.key
								? { ...existingLabel, values: [] }
								: existingLabel
						)
					: latestLabels.filter((existingLabel) => existingLabel.key !== labelToDelete.key);

			set({
				labels: updatedLabels,
				labelToDelete: null,
				deleteError: null,
				isPanelOpen: false,
				activeLabel: null,
				isDeleting: false
			});
			return true;
		} catch (error) {
			Logger.error('LabelManagementStore -> deleteLabel failed:', error);
			set({
				isDeleting: false,
				deleteError: DELETE_LABEL_MODAL_LABELS.DELETE_FAILED
			});
			return false;
		}
	},

	openAddPanel: () => {
		set({ isPanelOpen: true, panelMode: PanelMode.ADD, activeLabel: null });
	},

	openEditPanel: (label: Label) => {
		set({ isPanelOpen: true, panelMode: PanelMode.EDIT, activeLabel: label });
	},

	closePanel: () => {
		set({ isPanelOpen: false, activeLabel: null });
	},

	requestDelete: (label: Label) => {
		set({ labelToDelete: label, deleteError: null });
	},

	cancelDelete: () => {
		set({ labelToDelete: null, deleteError: null });
	},

	reset: () => {
		set(initialState);
	}
}));
